import type { GridInitialState, GridSortModel } from '../lib/dataGrid';
import { AUDIT_FIELDS } from './auditFields';
import { defineStoredJson } from '../utils/storage';

// Per-grid layout preferences, for the user on THIS DEVICE — a column layout is
// device-shaped, so this deliberately does NOT sync. KEPT: visibility, order,
// widths, sort model. NOT KEPT: filters and the quick-filter text, which read as
// data loss weeks later rather than as a restored preference.

// Renamed from `ast:gridPrefs` when every key moved to one prefix.
const prefsStore = defineStoredJson<Store>({
    key: 'grid_prefs',
    legacyKeys: ['ast:gridPrefs'],
    fallback: () => ({}),
    // Corrupt or foreign storage must never take a grid down.
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Store)
            : undefined
});

/**
 * Bump when the DEFAULT column sets change in a way that makes a stored order
 * misleading — a stored `orderedFields` cannot place a column it has never heard
 * of. Visibility and sorting survive a bump (see {@link readPrefs}); only the
 * order and widths are dropped.
 */
/**
 * v2: the audit columns + the computed KW column, in every office grid at once.
 * v3: Anzahl in the Geräte-Zeiterfassung.
 * v4: Vorläufige Datierung MOVED on the Befundliste — a stored order places it
 *     at the OLD position, which is the whole thing that changed.
 * v5: the Teil column joined the Befunde tree.
 * v6: the Typ column joined the Befunde tree AND the Befundliste, where its
 *     POSITION between Befundnummer and Interpretation is the point (as v4).
 * v7: Firmen-E-Mail and Visitenkarten joined the Benutzer grid.
 */
const VERSION = 7;

type GridColumnsState = NonNullable<GridInitialState['columns']>;

export interface GridPreferences {
    v: number;
    columnVisibilityModel?: GridColumnsState['columnVisibilityModel'];
    orderedFields?: GridColumnsState['orderedFields'];
    dimensions?: GridColumnsState['dimensions'];
    sortModel?: GridSortModel;
    /** Which {@link REVEALED} columns this layout has already been shown. */
    revealed?: string[];
    /** Which {@link RESORTED} default sort this layout has already been given. */
    resorted?: number;
}

/**
 * Columns a grid used to ship HIDDEN and now ships SHOWN: the stored model holds
 * an explicit `false` from the old default, so the new one never reaches the
 * user. Applied ONCE per grid and column, recorded in `revealed`, so a user who
 * hides it again keeps it hidden.
 */
const REVEALED: Record<string, readonly string[]> = {
    // Vorläufige Datierung joined the Befundliste's printed columns (0.63.0).
    // Planum/Profil was hidden by mistake in 0.95.0, together with the
    // Beschreibung column that shared its field (see findingDetailColumns).
    findingList: ['preliminaryDating', 'description'],
    'analysis:findingList': ['preliminaryDating', 'description']
};

/**
 * Grids whose DEFAULT sort has changed, and the generation of that change — the
 * sort-model counterpart of {@link REVEALED}. A stored model wins over the
 * grid's default and is written the first time the user touches ANY of the
 * layout. Applied ONCE per generation, recorded in `resorted`.
 */
const RESORTED: Record<string, number> = {
    // The Tagebuch reads newest day first (0.71.0).
    diary: 1,
    'analysis:diary': 1
};

type Store = Record<string, GridPreferences>;

function readStore(): Store {
    return prefsStore.read();
}

/**
 * A stored visibility model with every unknown column that must start hidden
 * forced hidden. A field MISSING from the model reads as VISIBLE, which is how a
 * stored model absorbs new columns but is wrong for the audit columns and
 * `hiddenByDefault`. Only absent fields are filled in.
 */
function withDefaultHiddenColumns(
    model: NonNullable<GridColumnsState['columnVisibilityModel']>,
    hiddenByDefault: readonly string[]
): NonNullable<GridColumnsState['columnVisibilityModel']> {
    const merged = { ...model };
    for (const field of [...AUDIT_FIELDS, ...hiddenByDefault]) {
        if (!(field in merged)) {
            merged[field] = false;
        }
    }
    return merged;
}

function writeStore(store: Store): void {
    // Private mode / quota — the grid just won't remember.
    prefsStore.write(store);
}

/**
 * The stored preferences for one grid as a partial grid state ready for
 * `restoreState`, or null when nothing usable is saved. `hiddenByDefault` names
 * the columns THIS grid ships hidden (see {@link withDefaultHiddenColumns}).
 */
export function readPrefs(
    gridId: string,
    hiddenByDefault: readonly string[] = []
): GridInitialState | null {
    const saved = readStore()[gridId];
    if (!saved) {
        return null;
    }
    // A stale version keeps what tolerates new columns (an unknown field falls
    // back to its own default in the visibility model) and drops what doesn't.
    const stale = saved.v !== VERSION;
    const columns: GridColumnsState = {};
    if (saved.columnVisibilityModel) {
        columns.columnVisibilityModel = withDefaultHiddenColumns(
            revealNewColumns(gridId, saved),
            hiddenByDefault
        );
    }
    if (!stale && saved.orderedFields) {
        columns.orderedFields = saved.orderedFields;
    }
    if (!stale && saved.dimensions) {
        columns.dimensions = saved.dimensions;
    }
    const state: GridInitialState = {};
    if (Object.keys(columns).length > 0) {
        state.columns = columns;
    }
    if (saved.sortModel && !dropStaleSort(gridId, saved)) {
        state.sorting = { sortModel: saved.sortModel };
    }
    return Object.keys(state).length > 0 ? state : null;
}

/**
 * A stored visibility model with this grid's newly-shown columns let through,
 * and the fact recorded so it happens once. The only read that WRITES, and it
 * has to: otherwise every mount would undo the user's next click.
 */
function revealNewColumns(
    gridId: string,
    saved: GridPreferences
): NonNullable<GridColumnsState['columnVisibilityModel']> {
    const model = { ...saved.columnVisibilityModel };
    const done = saved.revealed ?? [];
    const fresh = (REVEALED[gridId] ?? []).filter(
        (field) => !done.includes(field)
    );
    if (fresh.length === 0) {
        return model;
    }
    for (const field of fresh) {
        delete model[field];
    }
    const store = readStore();
    if (store[gridId]) {
        store[gridId] = {
            ...store[gridId],
            columnVisibilityModel: model,
            revealed: [...done, ...fresh]
        };
        writeStore(store);
    }
    return model;
}

/**
 * Whether this layout's stored sort model is a generation behind {@link
 * RESORTED} — and if so, records that it has been dropped, so the grid's new
 * default applies exactly once. Writes on read for the same reason {@link
 * revealNewColumns} does.
 */
function dropStaleSort(gridId: string, saved: GridPreferences): boolean {
    const generation = RESORTED[gridId];
    if (generation === undefined || saved.resorted === generation) {
        return false;
    }
    const store = readStore();
    if (store[gridId]) {
        store[gridId] = {
            ...store[gridId],
            sortModel: undefined,
            resorted: generation
        };
        writeStore(store);
    }
    return true;
}

export function writePrefs(gridId: string, state: GridInitialState): void {
    const store = readStore();
    store[gridId] = {
        v: VERSION,
        columnVisibilityModel: state.columns?.columnVisibilityModel,
        orderedFields: state.columns?.orderedFields,
        dimensions: state.columns?.dimensions,
        sortModel: state.sorting?.sortModel,
        /**
         * Carried over, so a reveal already applied is not applied twice. A grid
         * with NOTHING stored counts as fully revealed: the new default was
         * shown the moment it mounted, so hiding it again is a real choice and
         * not the stale `false` a reveal exists to drop.
         */
        revealed: store[gridId]?.revealed ?? [...(REVEALED[gridId] ?? [])],
        // Same reasoning for the default sort.
        resorted: store[gridId]?.resorted ?? RESORTED[gridId]
    };
    writeStore(store);
}

export function clearPrefs(gridId: string): void {
    const store = readStore();
    delete store[gridId];
    writeStore(store);
}

export function hasPrefs(gridId: string): boolean {
    return Boolean(readStore()[gridId]);
}
